import React from "react";
import {
  Alert,
  Grid,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
  IconButton,
} from "@mui/material";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { getCountryLabel } from "../../utils/countryUtils";
import { getCurrencyIcon } from "../../utils/currencyUtils";
import { components } from "../../types/api";

import PercentIcon from "@mui/icons-material/Percent";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import BarChartIcon from "@mui/icons-material/BarChart";
import FlagIcon from "@mui/icons-material/Flag";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

import { useUserRole } from "../../hooks/useUserRole";
import { canViewGoalField } from "../../utils/briefingPermissions";

interface AssociatedCampaignGoalSectionProps {
  goal: components["schemas"]["GoalResponse"] | null;
  onCreateGoal?: () => void;
  onEditGoal?: () => void;
  onDeleteGoal?: () => void;
  isEditMode?: boolean;
}

const AssociatedCampaignGoalSection: React.FC<
  AssociatedCampaignGoalSectionProps
> = ({ goal, onCreateGoal, onEditGoal, onDeleteGoal, isEditMode = false }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const { role } = useUserRole();

  // Role-based visibility check using centralized permission logic
  const shouldShowField = (fieldKey: string): boolean => {
    if (!role) return false;
    return canViewGoalField(role, fieldKey);
  };

  const renderDetailField = (
    fieldKey: string,
    label: string,
    value: string | number | null | undefined,
    isNumeric = false,
    isCurrency = false
  ): React.ReactNode => {
    if (!shouldShowField(fieldKey)) return null;

    let displayValue: string | number = value ?? "";
    if (value === null || value === undefined || value === "") {
      displayValue = t("common.noData", "No data available");
    } else if (isNumeric && typeof value === "number") {
      const precision =
        fieldKey === "expectedVolume"
          ? 0
          : fieldKey.toLowerCase().includes("cost")
            ? 4
            : 2;

      if (isCurrency && goal?.currency) {
        displayValue = new Intl.NumberFormat(undefined, {
          style: "currency",
          currency: goal.currency,
          minimumFractionDigits: 0,
          maximumFractionDigits: precision,
        }).format(value);
      } else {
        displayValue = new Intl.NumberFormat("es-ES", {
          minimumFractionDigits: 0,
          maximumFractionDigits: precision,
        }).format(value);
        if (isCurrency) {
          displayValue = `${displayValue} ${goal?.currency || ""}`.trim();
        }
      }
    }

    const iconConfig = (() => {
      switch (fieldKey) {
        case "totalInversion":
        case "platformInversion":
        case "grossUnitCost":
        case "netUnitCost":
          return {
            icon: getCurrencyIcon(goal?.currency, "small"),
            color: "primary.main",
          };
        case "margin":
        case "ctr":
          return {
            icon: <PercentIcon fontSize="small" />,
            color: "secondary.main",
          };
        case "expectedVolume":
          return {
            icon: <BarChartIcon fontSize="small" />,
            color: "info.main",
          };
        case "startDate":
        case "endDate":
          return {
            icon: <CalendarMonthIcon fontSize="small" />,
            color: "success.main",
          };
        case "objective":
        case "funnel":
          return { icon: <FlagIcon fontSize="small" />, color: "warning.main" };
        default:
          return null;
      }
    })();

    return (
      <Grid item xs={12} sm={6} md={4} key={label}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 1,
            transition: "box-shadow 0.2s",
            "&:hover": { boxShadow: theme.shadows[2] },
            bgcolor: theme.palette.background.default,
          }}
        >
          {iconConfig ? (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              color={iconConfig.color}
            >
              {iconConfig.icon}
              <Typography variant="subtitle2" fontWeight={600}>
                {label}
              </Typography>
            </Stack>
          ) : (
            <Typography
              variant="subtitle2"
              color="text.secondary"
              fontWeight={600}
            >
              {label}
            </Typography>
          )}
          <Typography
            variant="body2"
            sx={{ wordBreak: "break-word", mt: "auto" }}
            fontWeight={isNumeric ? 600 : 400}
          >
            {displayValue}
          </Typography>
        </Paper>
      </Grid>
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${theme.palette.grey[200]}`,
        p: { xs: 2, md: 4 },
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Typography variant="h6" fontWeight={700}>
          {t("briefingsView.goalSectionTitle", "Goal")}
        </Typography>
        {goal ? (
          <Stack direction="row" spacing={1}>
            {isEditMode && (
              <Tooltip title={t("common.edit", "Edit")}>
                <span>
                  <IconButton
                    color="primary"
                    onClick={onEditGoal}
                    disabled={!onEditGoal}
                    sx={{ bgcolor: "action.hover" }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <Tooltip title={t("briefingsView.deleteGoal", "Delete goal")}>
              <span>
                <IconButton
                  color="error"
                  onClick={onDeleteGoal}
                  disabled={!onDeleteGoal}
                  sx={{
                    "&:hover": { bgcolor: "error.main", color: "white" },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        ) : (
          <Tooltip title={t("briefingsView.createGoal", "Create goal")}>
            <span>
              <IconButton
                color="primary"
                onClick={onCreateGoal}
                disabled={!onCreateGoal}
                sx={{
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                  "&:hover": { bgcolor: "primary.dark" },
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Stack>

      {!goal ? (
        <Alert severity="info">
          {t(
            "briefingsView.goalNotFound",
            "This associated campaign does not have a goal yet."
          )}
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {renderDetailField(
            "campaign",
            t("briefingsView.goalForm.campaign", "Campaign"),
            goal.campaign
          )}
          {renderDetailField(
            "startDate",
            t("briefingsView.goalForm.startDate", "Start date"),
            goal.startDate ? dayjs(goal.startDate).format("YYYY-MM-DD") : null
          )}
          {renderDetailField(
            "endDate",
            t("briefingsView.goalForm.endDate", "End date"),
            goal.endDate ? dayjs(goal.endDate).format("YYYY-MM-DD") : null
          )}
          {renderDetailField(
            "country",
            t("briefingsView.goalForm.country", "Country"),
            getCountryLabel(goal.country)
          )}
          {renderDetailField(
            "objective",
            t("briefingsView.goalForm.objective", "Objective"),
            goal.objective
          )}
          {renderDetailField(
            "funnel",
            t("briefingsView.goalForm.funnel", "Funnel"),
            goal.funnel
          )}
          {renderDetailField(
            "status",
            t("briefingsView.goalForm.status", "Status"),
            goal.status ?? "-"
          )}
          {renderDetailField(
            "orderName",
            t("briefingsView.goalForm.orderName", "Order name"),
            goal.orderName
          )}
          {renderDetailField(
            "lineName",
            t("briefingsView.goalForm.lineName", "Line name"),
            goal.lineName
          )}
          {renderDetailField(
            "margin",
            t("briefingsView.goalForm.margin", "Margin"),
            goal.margin,
            true
          )}
          {renderDetailField(
            "expectedVolume",
            t("briefingsView.goalForm.expectedVolume", "Expected volume"),
            goal.expectedVolume,
            true
          )}
          {renderDetailField(
            "visibility",
            t("briefingsView.goalForm.visibility", "Visibility"),
            goal.visibility,
            true
          )}
          {renderDetailField(
            "ctr",
            t("briefingsView.goalForm.ctr", "CTR"),
            goal.ctr,
            true
          )}
          {renderDetailField(
            "netUnitCost",
            t("briefingsView.goalForm.netUnitCost", "Net unit cost"),
            goal.netUnitCost,
            true,
            true
          )}
          {renderDetailField(
            "zeotapDataCost",
            t("briefingsView.goalForm.zeotapDataCost", "Zeotap data cost"),
            goal.zeotapDataCost,
            true,
            true
          )}
          {renderDetailField(
            "adserverCost",
            t("briefingsView.goalForm.adserverCost", "Adserver cost"),
            goal.adserverCost,
            true,
            true
          )}
          {renderDetailField(
            "impressionTrackerCost",
            t(
              "briefingsView.goalForm.impressionTrackerCost",
              "Impression tracker cost"
            ),
            goal.impressionTrackerCost,
            true,
            true
          )}
          {renderDetailField(
            "clickTrackerCost",
            t("briefingsView.goalForm.clickTrackerCost", "Click tracker cost"),
            goal.clickTrackerCost,
            true,
            true
          )}

          {renderDetailField(
            "adtechCost",
            t("briefingsView.goalForm.adtechCost", "Adtech cost"),
            goal.adtechCost,
            true,
            true
          )}
          {renderDetailField(
            "grossUnitCost",
            t("briefingsView.goalForm.grossUnitCost", "Gross unit cost"),
            goal.grossUnitCost,
            true,
            true
          )}
          {renderDetailField(
            "platformInversion",
            t("briefingsView.goalForm.platformInversion", "Platform inversion"),
            goal.platformInversion,
            true,
            true
          )}
          {renderDetailField(
            "totalInversion",
            t("briefingsView.goalForm.totalInversion", "Total inversion"),
            goal.totalInversion,
            true,
            true
          )}
          {renderDetailField(
            "notes",
            t("briefingsView.goalForm.notes", "Notes"),
            goal.notes ?? "-"
          )}
          {renderDetailField(
            "addedOn",
            t("briefingsView.addedOn", "Added on"),
            dayjs(goal.createdAt).format("YYYY-MM-DD HH:mm:ss")
          )}
          {renderDetailField(
            "updatedOn",
            t("briefingsView.updatedOn", "Last updated"),
            dayjs(goal.updatedAt).format("YYYY-MM-DD HH:mm:ss")
          )}
        </Grid>
      )}
    </Paper>
  );
};

export default AssociatedCampaignGoalSection;
